import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/site';
import { useSeo } from '@/hooks/useSeo';

const milestones = [
  { year: '2021', title: '从一个收藏夹开始', desc: '最初只是几个人私下整理的话题清单，用来避免重复查找同一件事。' },
  { year: '2023', title: '上线专题合集结构', desc: '把零散条目按主题归拢，引入分类、更新时间和状态标记。' },
  { year: '2025', title: '加入分类榜单', desc: '在专题之外补充排序维度，让读者既能系统看，也能快速抓重点。' },
  { year: '2026', title: '持续扩展分类', desc: '五个分类稳定运转，专题每周保持更新，逐步补充慢热但有价值的话题线。' },
];

const principles = [
  { icon: 'ri-shield-check-line', title: '归类不代替判断', desc: '我们把同类内容收在一起，但不替读者下结论，不同说法之间保持并列关系。' },
  { icon: 'ri-links-line', title: '标注来源与时间', desc: '每条专题都明确更新日期与条目数量，第三方口径的数据尽量标注出处。' },
  { icon: 'ri-contrast-drop-2-line', title: '拒绝标题化夸大', desc: '标题只描述内容范围，不用情绪化措辞制造预期落差。' },
  { icon: 'ri-refresh-line', title: '更新可见可查', desc: '热门专题持续补充，结束的盘点明确标为已完结，不做虚假的「持续更新」。' },
];

const voices = [
  { quote: '最省时间的用法就是直接进专题，看完一个分类基本就掌握全貌了。', name: '读者 周迟', tag: '使用 2 年' },
  { quote: '榜单的排序维度写得很清楚，不像别的站只给一个名次让人猜。', name: '读者 沈禾', tag: '使用 1 年' },
  { quote: '专题会持续更新这点很实在，回头再看真的多了不少新条目。', name: '读者 高朗', tag: '使用 8 个月' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 黑料社网专题合集内容门户',
    description:
      '黑料社网是一个以专题合集与精选盘点为核心的内容门户。这里介绍我们的品牌定位、内容原则、发展历程与联系方式，欢迎推荐值得整理的专题。',
    keywords: '关于黑料社网,内容门户,内容原则,网站介绍,联系方式',
    canonicalPath: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们',
        url: 'https://jinpenge.cn/about',
        description: '黑料社网的品牌定位、内容原则、发展历程与联系方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteConfig.name,
        url: 'https://jinpenge.cn/',
        email: siteConfig.email,
        foundingDate: siteConfig.since,
        description: siteConfig.description,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jinpenge.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://jinpenge.cn/about' },
        ],
      },
    ],
  });

  return (
    <div>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-10 py-12 md:py-16">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950">关于黑料社网</h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            我们是一个以专题合集与精选盘点为核心的内容门户。做的是一件很具体的事：把散落各处的话题收拢成能一次看完的合集。
          </p>
        </div>
      </div>

      <section className="px-4 md:px-10 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <span className="text-xs tracking-[0.3em] text-primary-600 font-medium">WHO WE ARE</span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950 leading-snug">
            品牌介绍与网站定位
          </h2>
          <div className="mt-5 space-y-4 text-sm md:text-base leading-loose text-foreground-700">
            <p>
              黑料社网成立于 {siteConfig.since} 年，最初只是几个人共用的一份话题清单。我们发现，同一类信息往往散落在很多地方，
              读者需要反复搜索才能拼出全貌，于是决定把它整理成结构化的专题合集。
            </p>
            <p>
              我们的定位很明确：<strong className="text-foreground-950">不做新闻首发，只做内容归类</strong>。
              当一件事已经发生并被广泛讨论之后，我们负责把它和同类的事放在一起，让你快速看到这件事在整个话题脉络里的位置。
            </p>
            <p>
              目前站内设有五个分类：热搜盘点、娱乐观察、网络话题、影像现场与文化盘点。每个分类下都有若干个持续更新的专题，
              以及一张对应维度的分类榜单。
            </p>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative w-full h-72 md:h-[420px] rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20neatly%20stacked%20cream%20paper%20folders%20and%20sage%20green%20book%2C%20amber%20desk%20lamp%20glow%2C%20minimal%20archive%20aesthetic%2C%20soft%20natural%20light%2C%20calm%20ordered%20mood%2C%20no%20text&width=900&height=1100&seq=hl-about-01&orientation=portrait"
              alt="黑料社网内容整理工作场景示意"
              title="黑料社网 内容整理与专题归类"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-10 py-14 md:py-20 bg-background-100 border-y border-background-200" aria-label="内容原则">
        <div className="max-w-3xl">
          <span className="text-xs tracking-[0.3em] text-primary-600 font-medium">CONTENT PRINCIPLES</span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">我们的内容原则</h2>
          <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
            四条原则贯穿从选题到发布的全过程。它们决定了我们收录什么、怎么标注，以及什么时候该停笔。
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {principles.map((p) => (
            <div key={p.title} className="rounded-lg bg-background-50 border border-background-200 p-6">
              <span className="w-11 h-11 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center text-xl">
                <i className={p.icon}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-10 py-14 md:py-20" aria-label="发展历程">
        <div className="max-w-3xl">
          <span className="text-xs tracking-[0.3em] text-primary-600 font-medium">MILESTONES</span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">发展理念与历程</h2>
          <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
            我们相信整理本身就是一种价值。内容越多，越需要有人负责把它归类清楚。
          </p>
        </div>
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {milestones.map((m) => (
            <li key={m.year} className="relative pt-6 border-t-2 border-primary-500">
              <span className="font-heading text-3xl font-black text-primary-500">{m.year}</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{m.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{m.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-4 md:px-10 py-14 md:py-20 bg-secondary-50 border-y border-secondary-200" aria-label="用户口碑">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <span className="text-xs tracking-[0.3em] text-secondary-800 font-medium">READER VOICES</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">读者怎么说</h2>
          </div>
          <p className="text-sm text-foreground-600 max-w-md leading-relaxed">
            以下是来自长期读者的反馈摘录，我们保留了原始表达，未做修饰。
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {voices.map((v) => (
            <figure key={v.name} className="rounded-lg bg-background-50 border border-secondary-200 p-6 flex flex-col justify-between">
              <i className="ri-double-quotes-l text-3xl text-secondary-400"></i>
              <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">{v.quote}</blockquote>
              <figcaption className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-900">{v.name}</span>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">{v.tag}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-10 py-14 md:py-20" aria-label="联系方式">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.3em] text-primary-600 font-medium">CONTACT</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">联系我们</h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              无论是推荐专题、指出错误，还是希望合作，都可以通过邮件联系。我们在工作日内会尽量回复。
            </p>
            <div className="mt-7 flex flex-col gap-3">
              <a
                href={`mailto:${siteConfig.email}`}
                className="inline-flex items-center gap-3 h-12 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                读者来信 {siteConfig.email}
              </a>
              <a
                href={`mailto:${siteConfig.cooperateEmail}`}
                className="inline-flex items-center gap-3 h-12 px-5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hand-heart-line"></i>
                合作联系 {siteConfig.cooperateEmail}
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: 'ri-quill-pen-line', title: '推荐专题', desc: '写清主题、大致条目范围与推荐理由，我们会评估是否立项。' },
              { icon: 'ri-error-warning-line', title: '内容纠错', desc: '发现标注有误或信息过期，注明专题名称与具体位置即可。' },
              { icon: 'ri-team-line', title: '内容合作', desc: '希望提供数据口径或联合整理专题，请附上简要说明。' },
              { icon: 'ri-time-line', title: '更新节奏', desc: '专题每天 09:00 与 20:00 更新，榜单按周与月更新。' },
            ].map((item) => (
              <div key={item.title} className="rounded-lg bg-background-100 border border-background-200 p-6">
                <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center text-xl">
                  <i className={item.icon}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 rounded-lg bg-foreground-950 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-black text-background-50">先从专题合集逛起</h2>
            <p className="mt-3 text-sm text-background-300 leading-relaxed max-w-2xl">
              五个分类、九个在更专题，全部按更新时间排列，挑一个感兴趣的主题就能一次看够。
            </p>
          </div>
          <Link
            to="/topics"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            浏览专题合集
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}